import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { T, F } from "@/lib/bricks";
import { toast } from "sonner";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Controls, Meta, StatusText, List, Section, Row, Empty, Loading, TextLink, usePalette, humanize } from "@/components/calm/Calm";
import ProgressNote from "@/components/developer/ProgressNote";
import {
  EMPTY_FORM, FALLBACK_KINDS, buildPayload, linkPlaceholder, measureLine, viewLine, wantsGroundWidth, filterVisits, ownerStats, syncNote, unlinkedReason, validateForm,
} from "@/components/owner/ownerSiteProgressModel";

const TABS = [{ key: 'awaiting', label: 'Awaiting outputs' }, { key: 'delivered', label: 'Delivered' }, { key: 'all', label: 'All' }];

// Title + meta | status on top; deliverables, note and actions below at full width.
const PHONE_CSS = `
.osp-attach { display: grid; grid-template-columns: minmax(140px, 180px) minmax(0, 1fr); gap: 8px; }
.osp-deliv { display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: 2px 10px; align-items: baseline; font-size: 13px; }
@media (max-width: 600px) {
  .osp-attach { grid-template-columns: minmax(0, 1fr); }
  .osp-attach > * { grid-column: auto !important; }
}`;

function AttachForm({ visit, kinds, p, onSaved, onCancel }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const field = { padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, background: p.surf, color: p.ink, fontFamily: F.display, fontSize: 13, outline: 'none', minWidth: 0 };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const bad = validateForm(form, kinds);
    if (bad) { setErr(bad); return; }
    setSaving(true);
    setErr(null);
    try {
      await api.post(`/dev/capture-visits/${visit.id}/deliverables`, buildPayload(form));
      setForm(EMPTY_FORM);
      onSaved();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`attach-form-${visit.id}`} className="osp-attach" style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${p.hair}` }}>
      <select aria-label="Deliverable type" value={form.kind} onChange={set('kind')} style={field}>
        {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
      </select>
      <input aria-label="Link" placeholder={linkPlaceholder(form.kind)} value={form.url} onChange={set('url')} style={field} />
      <input aria-label="Label" placeholder="Label (optional)" value={form.label} onChange={set('label')} style={field} />
      <input aria-label="Preview image" placeholder="Preview image https:// (for compare and measuring)" value={form.preview_url} onChange={set('preview_url')} style={field} />
      {wantsGroundWidth(form) ? (
        <input aria-label="Ground width in metres" inputMode="decimal" placeholder="Ground width in metres (only if not a GeoTIFF)" value={form.ground_width_m} onChange={set('ground_width_m')} style={{ ...field, gridColumn: '1 / -1' }} />
      ) : null}
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 12, alignItems: 'center' }}>
        <button type="submit" disabled={saving} style={{ padding: '7px 14px', border: `1px solid ${p.ink}`, borderRadius: 2, background: p.ink, color: p.bg, fontFamily: F.display, fontSize: 12, cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Attach'}
        </button>
        <button type="button" onClick={onCancel} style={{ border: 'none', background: 'none', color: p.ink3, fontSize: 12, cursor: 'pointer', padding: 0 }}>Cancel</button>
        {err ? <span role="alert" style={{ fontSize: 12, color: p.ink }}>{err}</span> : null}
      </div>
    </form>
  );
}

function VisitRow({ v, kinds, p, open, onToggle, onChanged, onNote }) {
  const remove = async (d) => {
    try {
      await api.delete(`/dev/capture-visits/${v.id}/deliverables/${d.id}`);
      toast.success('Removed');
      onChanged();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail));
    }
  };
  const delivs = v.deliverables || [];
  const delivered = delivs.length || v.media_delivery_url;
  const awaiting = !delivered && v.status !== 'failed';
  return (
    <div data-testid={`site-visit-${v.id}`} style={{ padding: '16px 4px', borderBottom: `1px solid ${p.hair}` }}>
      <div className="calm-row">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: p.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.project_name || v.project_address || 'Project'}</div>
          <Meta style={{ marginTop: 3 }} parts={[
            { text: v.date ? fmtDay(v.date) : 'No date', figure: !!v.date },
            ...[v.service_label, v.company, v.builder_email].filter(Boolean).map(t => ({ text: t })),
            v.livemode === false ? { text: 'Test' } : null,
          ].filter(Boolean)} />
        </div>
        <div className="calm-status"><StatusText attention={awaiting}>{awaiting ? 'Awaiting outputs' : delivered ? 'Delivered' : humanize(v.status)}</StatusText></div>
      </div>
      {delivered ? (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {delivs.map((d) => (
            <div key={d.id} className="osp-deliv">
              <span style={{ fontSize: 12, color: p.ink4 }}>{d.kind_label}</span>
              <span style={{ minWidth: 0, display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <a href={d.url} target="_blank" rel="noreferrer" style={{ color: p.ink, textDecoration: 'none', borderBottom: `1px solid ${p.hairS}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</a>
                {measureLine(d) ? <span style={{ fontSize: 11.5, color: p.ink4 }}>{measureLine(d)}</span> : null}
                {viewLine(d) ? <span style={{ fontSize: 11.5, color: p.ink4, overflowWrap: 'anywhere' }}>{viewLine(d)}</span> : null}
                <button type="button" onClick={() => remove(d)} aria-label={`Remove ${d.label}`} style={{ border: 'none', background: 'none', color: p.ink4, fontSize: 11.5, cursor: 'pointer', padding: 0 }}>Remove</button>
              </span>
            </div>
          ))}
          {v.media_delivery_url ? (
            <div className="osp-deliv">
              <span style={{ fontSize: 12, color: p.ink4 }}>Delivery</span>
              <a href={v.media_delivery_url} target="_blank" rel="noreferrer" style={{ color: p.ink3, textDecoration: 'none' }}>Delivery link</a>
            </div>
          ) : null}
        </div>
      ) : null}
      <ProgressNote note={v.progress_note} canAdd={v.status !== 'failed'}
        tone={{ ink: p.ink, muted: p.ink3, line: p.hairS, accent: T.brick, surface: p.surf, solidText: p.bg }}
        onSave={async (text) => {
          try {
            const r = await api.put(`/dev/owner/capture-visits/${v.id}/progress-note`, { text });
            onNote(v.id, (r.data && r.data.progress_note) || null);
          } catch (ex) {
            throw new Error(formatApiErrorDetail(ex?.response?.data?.detail) || 'Couldn’t save the note. Try again.');
          }
        }} />
      {open ? <AttachForm visit={v} kinds={kinds} p={p} onSaved={() => { toast.success('Attached'); onChanged(); }} onCancel={onToggle} /> : (
        <div style={{ marginTop: 10 }}><TextLink onClick={onToggle}>Add deliverable</TextLink></div>
      )}
    </div>
  );
}

export default function OwnerSiteProgressPage() {
  const { p } = usePalette();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('awaiting');
  const [openId, setOpenId] = useState(null);

  const load = (quiet) => {
    if (!quiet) setLoading(true);
    return api.get('/dev/owner/site-visits')
      .then((r) => { setData(r.data); setError(null); })
      .catch((ex) => {
        const s = ex?.response?.status;
        setError(s === 401 || s === 403 ? 'Sign in as platform owner.' : formatApiErrorDetail(ex?.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Loading />;

  const visits = (data && data.visits) || [];
  const unlinked = (data && data.unlinked) || [];
  const kinds = (data && data.kinds && data.kinds.length) ? data.kinds : FALLBACK_KINDS;
  const stats = ownerStats(visits, unlinked);
  const shown = filterVisits(visits, tab);
  const note = syncNote(data && data.sync);
  const notices = [note, data && data.deliverables_error, data && data.notes_error].filter(Boolean);

  return (
    <Page testid="owner-site-progress">
      <style>{PHONE_CSS}</style>
      <PageHeader title="Site progress" subtitle={`Builder visits and what they receive. ${stats.visits} visit${stats.visits === 1 ? '' : 's'} so far.`} />

      {error ? <div role="alert" style={{ fontSize: 13, color: T.brickDeep }}>{error}</div> : null}

      <Stats items={[
        { label: 'Awaiting outputs', value: stats.awaiting, attention: stats.awaiting > 0 },
        { label: 'Delivered', value: stats.delivered },
        { label: 'Not linked', value: stats.unlinked, attention: stats.unlinked > 0 },
      ]} />

      <Controls>
        <StatusTabs options={TABS} value={tab} onChange={setTab} testid="site-progress-filter" />
        {notices.length ? <div style={{ fontSize: 12.5, color: p.ink3 }}>{notices.join('. ')}</div> : null}
      </Controls>

      {shown.length === 0 ? (
        <Empty>{tab === 'awaiting' ? 'Nothing is waiting on outputs.' : tab === 'delivered' ? 'Nothing delivered yet.' : 'No site visits yet. Builder bookings appear here once they are scheduled.'}</Empty>
      ) : (
        <List>
          {shown.map((v) => (
            <VisitRow key={v.id} v={v} kinds={kinds} p={p} open={openId === v.id}
              onToggle={() => setOpenId(openId === v.id ? null : v.id)}
              onChanged={() => { setOpenId(null); load(true); }}
              onNote={(id, nt) => setData((d) => ({ ...d, visits: (d.visits || []).map((x) => (x.id === id ? { ...x, progress_note: nt } : x)) }))} />
          ))}
        </List>
      )}

      {unlinked.length ? (
        <Section title="Not on a timeline yet" note="Builder bookings that haven't been linked to a project.">
          <List>
            {unlinked.map((b) => (
              <Row
                key={b.id}
                title={b.company || b.contact_email || 'Builder booking'}
                meta={[b.company ? b.contact_email : null, b.site_address, b.service_label, b.livemode === false ? 'Test' : null].filter(Boolean)}
                next={[{ text: b.preferred_date ? `Preferred ${fmtDay(b.preferred_date)}` : 'No preferred date' }, b.has_builder_account ? null : { text: unlinkedReason(b) }].filter(Boolean)}
                status={b.has_builder_account ? 'Waiting to link' : 'No account'}
                attention={!b.has_builder_account}
              />
            ))}
          </List>
        </Section>
      ) : null}
    </Page>
  );
}
